/**
 * propertyRequestService.js
 *
 * All API calls for the PropertyRequest wizard.
 * Maps to PropertyRequestViewSet endpoints in apps/ms/views.py.
 *
 * Base URL: /api/ms/requests/
 *
 * Endpoints covered:
 *   POST   /                              → createDraft
 *   PATCH  /{id}/                         → updateDraft
 *   GET    /{id}/                         → getRequest
 *   GET    /                              → listRequests
 *   PUT    /{id}/amenities/               → upsertAmenities
 *   PUT    /{id}/geo_features/            → upsertGeoFeatures
 *   GET    /{id}/commute-anchors/         → listAnchors
 *   POST   /{id}/commute-anchors/add/     → addAnchor
 *   PATCH  /{id}/commute-anchors/{aid}/edit/   → updateAnchor
 *   DELETE /{id}/commute-anchors/{aid}/remove/ → deleteAnchor
 *   POST   /{id}/submit/                  → submitRequest
 */

import api from '../httpClient';
import apiRoutes from '../apiRoutes';

const { requests } = apiRoutes;

// ── Draft lifecycle ──────────────────────────────────────────────────────────

/** Create a new DRAFT request. Returns the full request object with id. */
export const createDraft = (data) =>
    api.post(requests.list, data).then(r => r.data);

/** Partial-update any DRAFT fields. */
export const updateDraft = (id, data) =>
    api.patch(requests.detail(id), data).then(r => r.data);

/** Fetch a single request by UUID. */
export const getRequest = (id) =>
    api.get(requests.detail(id)).then(r => r.data);

/** List all requests belonging to the current user. */
export const listRequests = () =>
    api.get(requests.list).then(r => r.data);

/** Delete a request by UUID. */
export const deleteRequest = (id) =>
    api.delete(requests.detail(id));

// ── Amenities ────────────────────────────────────────────────────────────────

/**
 * Full-replace amenity list for a request.
 * items: [{ amenity: id, rank: 1, required: bool, value: any }]
 */
export const upsertAmenities = (id, items) =>
    api.put(requests.amenities(id), { items }).then(r => r.data);

// ── Geo features ─────────────────────────────────────────────────────────────

/**
 * Full-replace geo feature list for a request.
 * items: [{ geo_feature: id, rank: 1, required: bool, value: float }]
 */
export const upsertGeoFeatures = (id, items) =>
    api.put(requests.geoFeatures(id), { items }).then(r => r.data);

// ── Commute anchors ──────────────────────────────────────────────────────────

/** List all commute anchors for a request. */
export const listAnchors = (id) =>
    api.get(requests.commuteAnchors(id)).then(r => r.data);

/**
 * Add a commute anchor to a request.
 * data: { label, location: {lat, lon}, mode, max_duration_min, days_per_week }
 */
export const addAnchor = (id, data) =>
    api.post(requests.addCommuteAnchor(id), data).then(r => r.data);

/** Partially update a commute anchor. */
export const updateAnchor = (id, anchorId, data) =>
    api.patch(requests.editCommuteAnchor(id, anchorId), data).then(r => r.data);

/** Delete a commute anchor. */
export const deleteAnchor = (id, anchorId) =>
    api.delete(requests.removeCommuteAnchor(id, anchorId));

// ── Submit ───────────────────────────────────────────────────────────────────

/**
 * Validate and submit the request. Transitions status → SUBMITTED.
 * Returns { status, submitted_at, context: snapshot }.
 */
export const submitRequest = (id) =>
    api.post(requests.submit(id), {}).then(r => r.data);

// ── Matching ─────────────────────────────────────────────────────────────────

/**
 * Run the matching engine for a submitted request.
 * Returns { total, page, results: [...listings] }
 */
export const getMatches = (id, page = 1, pageSize = 20) =>
    api.get(requests.matches(id), { params: { page, page_size: pageSize } }).then(r => r.data);
